import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { uniqueId } from 'lodash-es';
import { TApiaFilter, TApiaFilterValue } from '../../types';
import searchMetaFilters from './api/searchMetaFilters';
import setFilters from './api/setFilters';
import { TDocumentsSlice, TDocumentState, TSetDocsInitialState } from './types';

const getInitialState: (props: TSetDocsInitialState) => TDocumentState = (
  initialState: TSetDocsInitialState,
) => {
  return {
    freeMetaFilters: [],
    freeEnvMetaFilters: [],
    allowFreeMeta: false,
    processInfo: '',
    docType: undefined,
    filters: [],
    filledMetaFilters: [],
    ...initialState,
  };
};

const documentsMonitorSlice = createSlice({
  name: 'documents',
  initialState: {
    metaFilters: {},
  } as TDocumentsSlice,
  reducers: {
    initState(
      state,
      {
        payload: { fieldId, initialState },
      }: PayloadAction<{
        fieldId: string;
        initialState?: TSetDocsInitialState;
      }>,
    ) {
      state.metaFilters[fieldId] = getInitialState({
        ...initialState,
      });
    },
    deleteFilters(
      state,
      { payload }: PayloadAction<{ fieldId: string; resetOrigin?: boolean }>,
    ) {
      state.metaFilters[payload.fieldId].filters = state.metaFilters[
        payload.fieldId
      ].filters.map((currentFilter) => {
        if (
          ['D', 'date'].includes(currentFilter.type as string) ||
          currentFilter.isRange
        ) {
          return {
            ...currentFilter,
            currentValue: '',
            filterToValue: '',
          };
        }
        return {
          ...currentFilter,
          currentValue: '',
        };
      });
      state.metaFilters[payload.fieldId].freeMetaFilters = [];
      state.metaFilters[payload.fieldId].filledMetaFilters = [];
      if (payload.resetOrigin) {
        state.metaFilters[payload.fieldId].documentsOrigin = '';
      }
    },
    setDocumentsOrigin: (
      state,
      {
        payload,
      }: PayloadAction<{
        fieldId: string;
        origin: TApiaFilterValue;
      }>,
    ) => {
      if (state.metaFilters[payload.fieldId]) {
        state.metaFilters[payload.fieldId].documentsOrigin = payload.origin;
      }
      if (
        ['', 'T', 'P', 'E', 'F', 'V', 'NA'].includes(String(payload.origin))
      ) {
        state.metaFilters[payload.fieldId].filters = state.metaFilters[
          payload.fieldId
        ].filters?.map((upperFilter) => {
          if (
            upperFilter.id === 'txtInst' ||
            (payload.origin === '' && upperFilter.id === 'txtRelTitle')
          ) {
            return {
              ...upperFilter,
              currentValue: '',
              readonly: true,
            };
          }

          return upperFilter;
        });
      }
    },
    updateMetaFilters: (
      state,
      {
        payload,
      }: PayloadAction<{
        fieldId: string;
        updatedFilter: TApiaFilter;
      }>,
    ) => {
      state.metaFilters[payload.fieldId].filters.forEach((filter, index) => {
        if (
          filter.id === payload.updatedFilter.id &&
          state.metaFilters[payload.fieldId]
        ) {
          state.metaFilters[payload.fieldId].filters[index] =
            payload.updatedFilter;
          if (
            filter.currentValue !== payload.updatedFilter.currentValue &&
            payload.updatedFilter.currentValue !== ''
          ) {
            if (
              !state.metaFilters[payload.fieldId].filledMetaFilters.includes(
                payload.updatedFilter.id as string,
              )
            ) {
              state.metaFilters[payload.fieldId].filledMetaFilters.push(
                payload.updatedFilter.id as string,
              );
            }
          } else if (
            state.metaFilters[payload.fieldId].filledMetaFilters.includes(
              payload.updatedFilter.id as string,
            )
          ) {
            state.metaFilters[payload.fieldId].filledMetaFilters =
              state.metaFilters[payload.fieldId].filledMetaFilters.filter(
                (filterId) => {
                  return filterId !== payload.updatedFilter.id;
                },
              );
          }
        }
      });
    },
    addFreeMetaFilters: (
      state,
      {
        payload,
      }: PayloadAction<{
        fieldId: string;
        newFreeFilter: TApiaFilter;
      }>,
    ) => {
      state.metaFilters[payload.fieldId].freeMetaFilters?.push(
        payload.newFreeFilter,
      );
    },
    updateFreeMetaFilters: (
      state,
      {
        payload,
      }: PayloadAction<{
        fieldId: string;
        updatedFilterId: string | number;
        title?: string;
        value?: string;
      }>,
    ) => {
      state.metaFilters[payload.fieldId].freeMetaFilters.forEach(
        (filter, index) => {
          if (filter.id === payload.updatedFilterId) {
            if (payload.title) {
              state.metaFilters[payload.fieldId].freeMetaFilters[index] = {
                ...state.metaFilters[payload.fieldId].freeMetaFilters[index],
                title: payload.title,
              };
            }
            if (payload.value) {
              state.metaFilters[payload.fieldId].freeMetaFilters[index] = {
                ...state.metaFilters[payload.fieldId].freeMetaFilters[index],
                currentValue: payload.value,
              };
            }
            if (
              (payload.title &&
                state.metaFilters[payload.fieldId].freeMetaFilters[index]
                  .currentValue !== '') ||
              (payload.value &&
                state.metaFilters[payload.fieldId].freeMetaFilters[index]
                  .title !== '')
            ) {
              if (
                !state.metaFilters[payload.fieldId].filledMetaFilters.includes(
                  payload.updatedFilterId as string,
                )
              ) {
                state.metaFilters[payload.fieldId].filledMetaFilters.push(
                  payload.updatedFilterId as string,
                );
              }
            } else if (payload.title === '' || payload.value === '') {
              state.metaFilters[payload.fieldId].filledMetaFilters =
                state.metaFilters[payload.fieldId].filledMetaFilters.filter(
                  (filterId) => {
                    return filterId !== payload.updatedFilterId;
                  },
                );
            }
          }
        },
      );
    },
    removeFreeMetaFilter: (
      state,
      {
        payload,
      }: PayloadAction<{
        fieldId: string;
        deletedFilterId: TApiaFilterValue;
      }>,
    ) => {
      state.metaFilters[payload.fieldId].freeMetaFilters.splice(
        state.metaFilters[payload.fieldId].freeMetaFilters.findIndex(
          (filter) => {
            return filter.id === payload.deletedFilterId;
          },
        ),
        1,
      );
      state.metaFilters[payload.fieldId].filledMetaFilters = state.metaFilters[
        payload.fieldId
      ].filledMetaFilters.filter((filterId) => {
        return filterId !== payload.deletedFilterId;
      });
    },
    addFreeEvtMetaFilters: (
      state,
      {
        payload,
      }: PayloadAction<{
        fieldId: string;
        newFreeFilter: TApiaFilter;
      }>,
    ) => {
      state.metaFilters[payload.fieldId].freeEnvMetaFilters?.push(
        payload.newFreeFilter,
      );
    },
    updateFreeEvtMetaFilters: (
      state,
      {
        payload,
      }: PayloadAction<{
        fieldId: string;
        updatedFilterId: string | number;
        title?: string;
        value?: string;
      }>,
    ) => {
      state.metaFilters[payload.fieldId].freeEnvMetaFilters.forEach(
        (filter, index) => {
          if (filter.id === payload.updatedFilterId) {
            if (payload.title) {
              state.metaFilters[payload.fieldId].freeEnvMetaFilters[index] = {
                ...state.metaFilters[payload.fieldId].freeEnvMetaFilters[index],
                title: payload.title,
              };
            }
            if (payload.value) {
              state.metaFilters[payload.fieldId].freeEnvMetaFilters[index] = {
                ...state.metaFilters[payload.fieldId].freeEnvMetaFilters[index],
                currentValue: payload.value,
              };
            }
            if (
              (payload.title &&
                state.metaFilters[payload.fieldId].freeEnvMetaFilters[index]
                  .currentValue !== '') ||
              (payload.value &&
                state.metaFilters[payload.fieldId].freeEnvMetaFilters[index]
                  .title !== '')
            ) {
              if (
                !state.metaFilters[payload.fieldId].filledMetaFilters.includes(
                  payload.updatedFilterId as string,
                )
              ) {
                state.metaFilters[payload.fieldId].filledMetaFilters.push(
                  payload.updatedFilterId as string,
                );
              }
            } else if (payload.title === '' || payload.value === '') {
              state.metaFilters[payload.fieldId].filledMetaFilters =
                state.metaFilters[payload.fieldId].filledMetaFilters.filter(
                  (filterId) => {
                    return filterId !== payload.updatedFilterId;
                  },
                );
            }
          }
        },
      );
    },
    removeFreeEvtMetaFilter: (
      state,
      {
        payload,
      }: PayloadAction<{
        fieldId: string;
        deletedFilterId: TApiaFilterValue;
      }>,
    ) => {
      state.metaFilters[payload.fieldId].freeEnvMetaFilters.splice(
        state.metaFilters[payload.fieldId].freeEnvMetaFilters.findIndex(
          (filter) => {
            return filter.id === payload.deletedFilterId;
          },
        ),
        1,
      );
      state.metaFilters[payload.fieldId].filledMetaFilters = state.metaFilters[
        payload.fieldId
      ].filledMetaFilters.filter((filterId) => {
        return filterId !== payload.deletedFilterId;
      });
    },
  },
  extraReducers(builder) {
    builder.addCase(searchMetaFilters.fulfilled, (state, payload) => {
      const settedFreeMetaFilters = state.metaFilters[
        payload.payload.fieldId
      ].freeMetaFilters.filter((filter) => filter.currentValue && filter.title);
      const settedMetaFilters = state.metaFilters[
        payload.payload.fieldId
      ].filters.filter((filter) => filter.currentValue);

      state.metaFilters[payload.payload.fieldId].freeMetaFilters = [];
      state.metaFilters[payload.payload.fieldId].allowFreeMeta =
        payload.payload.allowFreeMeta;
      state.metaFilters[payload.payload.fieldId].allowEnvFreeMeta =
        payload.payload.allowEnvFreeMeta;
      if (payload.payload.allowFreeMeta) {
        state.metaFilters[payload.payload.fieldId].freeMetaFilters.push({
          id: uniqueId(),
          currentValue: '',
          title: '',
        });
      }
      if (payload.payload.allowEnvFreeMeta) {
        state.metaFilters[payload.payload.fieldId].freeEnvMetaFilters.push({
          id: uniqueId(),
          currentValue: '',
          title: '',
        });
      }
      if (payload.payload.processInfo) {
        state.metaFilters[payload.payload.fieldId].processInfo =
          payload.payload.processInfo;
      }
      state.metaFilters[payload.payload.fieldId].filters = [
        ...payload.payload.newFilters,
      ];
      state.metaFilters[payload.payload.fieldId].docType =
        payload.payload.docType;
      state.metaFilters[payload.payload.fieldId].filledMetaFilters =
        state.metaFilters[payload.payload.fieldId].filledMetaFilters.filter(
          (filterId) => {
            return !(
              settedFreeMetaFilters
                .map((freeMetaFilter) => freeMetaFilter.id)
                .includes(filterId) ||
              settedMetaFilters
                .map((metaFilter) => metaFilter.id)
                .includes(filterId)
            );
          },
        );
    });
    builder.addCase(searchMetaFilters.rejected, (_, payload) => {
      console.error(payload.error);
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    builder.addCase(setFilters.fulfilled, (_, __) => {});
    builder.addCase(setFilters.rejected, (_, payload) => {
      console.error(payload.error);
    });
  },
});

export const documentsMonitorActions = {
  ...documentsMonitorSlice.actions,
  searchMetaFilters,
  setFilters,
};
export default documentsMonitorSlice.reducer;
